import { useState, useMemo, useRef, useCallback } from "react";
import { getWeekStartString } from "@/lib/week";
import { resolveDayStatus } from "@/lib/presence";
import { useAlternanceJours } from "@/hooks/use-alternance";
import {
  useProfiles, useTechnicianSchedules, useCreateSchedule, useUpdateSchedule,
  useUpsertScheduleDay,
  useCreateTechnician, useCreateCommercial, useCreateAdminMember,
  useDocuments, useUploadDocument, useDeleteDocument, useUploadAvatar,
  useUpdateProfile, useDeleteProfile, useBatchCreateSchedules,
  type DocumentInfo,
} from "@/hooks/use-profiles";
import { useAgences } from "@/hooks/use-agences";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft, ChevronRight, Users, Building2, Wifi, UserX,
  Plus, Upload, Trash2, FileText, Copy, Camera, Loader2,
  Pencil, Calendar, CalendarRange, LayoutTemplate, Save,
  Palmtree, GraduationCap, HardHat, TrendingUp, Briefcase, Crown,
  RefreshCw, CheckCircle2, AlertTriangle, Bot, Mail, Shield, UserCheck,
  UserMinus, CircleSlash, Search, X as XIcon,
} from "lucide-react";
import type { Profile, TechnicianSchedule } from "@shared/schema";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { getRoleLevel, ACCESS_ROLE_LABELS, canDo, isSuperAdmin } from "@shared/roles";

function accessRoleBadgeColor(role: string | undefined): string {
  switch (role) {
    case "super_admin": return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800";
    case "admin": return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";
    case "manager": return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800";
    default: return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800/40 dark:text-gray-400 dark:border-gray-700";
  }
}

const DAYS = [
  { key: "mondayStatus", label: "Lun", full: "Lundi" },
  { key: "tuesdayStatus", label: "Mar", full: "Mardi" },
  { key: "wednesdayStatus", label: "Mer", full: "Mercredi" },
  { key: "thursdayStatus", label: "Jeu", full: "Jeudi" },
  { key: "fridayStatus", label: "Ven", full: "Vendredi" },
] as const;

type DayKey = typeof DAYS[number]["key"];

const STATUS_CONFIG = {
  bureau: { label: "Bureau", icon: Building2, color: "bg-green-500", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", border: "border-green-200 dark:border-green-800" },
  remote: { label: "Remote", icon: Wifi, color: "bg-blue-500", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400", border: "border-blue-200 dark:border-blue-800" },
  absent: { label: "Absent", icon: UserX, color: "bg-rose-500", bg: "bg-rose-100 dark:bg-rose-900/30", text: "text-rose-700 dark:text-rose-400", border: "border-rose-200 dark:border-rose-800" },
  conges: { label: "Congés", icon: Palmtree, color: "bg-orange-500", bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400", border: "border-orange-200 dark:border-orange-800" },
  alternance: { label: "Alternance", icon: GraduationCap, color: "bg-gray-500", bg: "bg-gray-100 dark:bg-gray-900/30", text: "text-gray-700 dark:text-gray-400", border: "border-gray-200 dark:border-gray-800" },
};

const COMMERCIAL_STATUS_LABELS: Record<string, string> = {
  salarie: "Salarié",
  agent_commercial: "Agent commercial",
  alternant: "Alternant",
};

const ADMIN_ROLE_PRESETS = [
  "Contrôleuse de gestion",
  "Assistante commerciale",
  "Responsable logistique",
  "Community manager",
  "Office manager",
  "Direction",
];

const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

type ServiceFilter = "all" | "technicien" | "commercial" | "administration" | "dirigeant";
type StatusFilter = "actifs" | "inactifs" | "tous";

const SERVICE_FILTERS: { key: ServiceFilter; label: string; icon: React.ElementType }[] = [
  { key: "all", label: "Tous", icon: Users },
  { key: "technicien", label: "Techniciens", icon: HardHat },
  { key: "commercial", label: "Commerciaux", icon: TrendingUp },
  { key: "administration", label: "Administratif", icon: Briefcase },
  { key: "dirigeant", label: "Dirigeants", icon: Crown },
];

function roleToService(role: string): string {
  switch (role) {
    case "technicien": return "Technicien";
    case "commercial": return "Commercial";
    case "administration": return "Administratif";
    case "gerant": return "Gérant";
    case "admin":
    case "planification": return "Dirigeant";
    default: return role;
  }
}

function serviceColor(role: string): string {
  switch (role) {
    case "technicien": return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800";
    case "commercial": return "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800";
    case "administration": return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800";
    case "gerant": return "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800";
    case "admin":
    case "planification": return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";
    default: return "bg-gray-100 text-gray-800 border-gray-200";
  }
}

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatWeekRange(monday: Date): string {
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  return `${monday.toLocaleDateString("fr-FR", opts)} — ${friday.toLocaleDateString("fr-FR", opts)} ${monday.getFullYear()}`;
}

function toDateString(d: Date): string {
  return getWeekStartString(d);
}

function getWeeksOfYear(year: number): Date[] {
  const weeks: Date[] = [];
  let d = new Date(year, 0, 1);
  d = getMonday(d);
  if (d.getFullYear() < year) d.setDate(d.getDate() + 7);
  while (d.getFullYear() === year) {
    weeks.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return weeks;
}

function getWeekNumber(monday: Date): number {
  const d = new Date(monday);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function getMondaysInRange(startDate: Date, endDate: Date): Date[] {
  const mondays: Date[] = [];
  let current = getMonday(new Date(startDate));
  const end = new Date(endDate);
  while (current <= end) {
    mondays.push(new Date(current));
    current.setDate(current.getDate() + 7);
  }
  return mondays;
}

function MembreFormDialog({
  open,
  onOpenChange,
  editingMembre,
  schedules,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editingMembre: Profile | null;
  schedules: TechnicianSchedule[];
}) {
  const { toast } = useToast();
  const { profile: currentUser } = useAuth();
  const isSuperAdminUser = isSuperAdmin((currentUser as any)?.accessRole);
  const createTechnician = useCreateTechnician();
  const createCommercial = useCreateCommercial();
  const createAdmin = useCreateAdminMember();
  const updateProfile = useUpdateProfile();
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const batchSchedules = useBatchCreateSchedules();
  const { data: agences } = useAgences();
  const uploadDocument = useUploadDocument();
  const deleteDocument = useDeleteDocument();
  const uploadAvatar = useUploadAvatar();
  const alternanceMap = useAlternanceJours();

  const isEdit = !!editingMembre;
  const profileId = editingMembre?.id || null;

  const [activeTab, setActiveTab] = useState("info");
  const [createdProfileId, setCreatedProfileId] = useState<string | null>(profileId);
  const [conflictProfile, setConflictProfile] = useState<{ profileId: string; isActive: boolean } | null>(null);
  const [planningMode, setPlanningMode] = useState<"week" | "annual" | "range" | "template">("week");

  const [firstName, setFirstName] = useState(editingMembre?.firstName || "");
  const [lastName, setLastName] = useState(editingMembre?.lastName || "");
  const [email, setEmail] = useState((editingMembre as any)?.email || "");
  const [phone, setPhone] = useState((editingMembre as any)?.phone || "");
  const [hasDrivingLicense, setHasDrivingLicense] = useState(editingMembre?.hasDrivingLicense || false);
  const [intervenantTerrain, setIntervenantTerrain] = useState<boolean>((editingMembre as any)?.intervenantTerrain ?? false);
  const [gestionPlanning, setGestionPlanning] = useState<boolean>((editingMembre as any)?.gestionPlanning ?? false);
  const [selectedRole, setSelectedRole] = useState(editingMembre?.role || "technicien");
  const [commercialStatus, setCommercialStatus] = useState((editingMembre as any)?.commercialStatus || "salarie");
  const [adminRole, setAdminRole] = useState((editingMembre as any)?.adminRole || "");
  const [rolesSecondaires, setRolesSecondaires] = useState<string[]>((editingMembre as any)?.rolesSecondaires || []);
  const [selectedAccessRole, setSelectedAccessRole] = useState<string>((editingMembre as any)?.accessRole || "technicien");
  const [selectedAgenceId, setSelectedAgenceId] = useState<string>((editingMembre as any)?.agenceId || "none");
  const [avatarPreview, setAvatarPreview] = useState<string | null>(editingMembre?.avatarUrl || null);

  const [scheduleMonday, setScheduleMonday] = useState(() => getMonday(new Date()));
  const [weekSchedule, setWeekSchedule] = useState<Record<DayKey, string>>({
    mondayStatus: "bureau", tuesdayStatus: "bureau", wednesdayStatus: "bureau",
    thursdayStatus: "bureau", fridayStatus: "bureau",
  });

  const [annualYear, setAnnualYear] = useState(new Date().getFullYear());
  const [selectedAnnualWeek, setSelectedAnnualWeek] = useState<Date | null>(null);

  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [rangeStatus, setRangeStatus] = useState("absent");

  const [templateWeek, setTemplateWeek] = useState<Record<DayKey, string>>({
    mondayStatus: "bureau", tuesdayStatus: "bureau", wednesdayStatus: "absent",
    thursdayStatus: "bureau", fridayStatus: "bureau",
  });
  const [templateStart, setTemplateStart] = useState("");
  const [templateEnd, setTemplateEnd] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: documents } = useDocuments(createdProfileId);

  const scheduleMap = useMemo(() => {
    const map = new Map<string, TechnicianSchedule>();
    schedules?.forEach(s => {
      map.set(`${s.technicianId}_${s.weekStart}`, s);
    });
    return map;
  }, [schedules]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
    if (createdProfileId) {
      try {
        await uploadAvatar.mutateAsync({ profileId: createdProfileId, file });
        toast({ title: "Photo mise à jour" });
      } catch {
        toast({ variant: "destructive", title: "Erreur upload photo" });
      }
    }
  };

  const isPending = createTechnician.isPending || createCommercial.isPending || createAdmin.isPending || updateProfile.isPending;

  const handleSaveProfile = async () => {
    if (!firstName || !lastName) {
      toast({ variant: "destructive", title: "Erreur", description: "Prénom et nom requis" });
      return;
    }

    try {
      if (isEdit && profileId) {
        await updateProfile.mutateAsync({
          id: profileId, firstName, lastName, email, phone,
          hasDrivingLicense,
          intervenantTerrain,
          gestionPlanning,
          role: selectedRole as any,
          rolesSecondaires,
          ...(selectedRole === "commercial" ? { commercialStatus } : {}),
          ...(selectedRole === "administration" ? { adminRole } : {}),
          ...(isSuperAdminUser ? { accessRole: selectedAccessRole } : {}),
          agenceId: selectedAgenceId === "none" ? null : selectedAgenceId,
        } as any);
        setCreatedProfileId(profileId);
        toast({ title: "Profil mis à jour" });
        setActiveTab("documents");
      } else {
        let profile: any;
        if (selectedRole === "technicien") {
          profile = await createTechnician.mutateAsync({ firstName, lastName, email, phone, hasDrivingLicense, agenceId: selectedAgenceId === "none" ? null : selectedAgenceId });
        } else if (selectedRole === "commercial") {
          profile = await createCommercial.mutateAsync({ firstName, lastName, email, phone, hasDrivingLicense, commercialStatus, agenceId: selectedAgenceId === "none" ? null : selectedAgenceId });
        } else if (selectedRole === "administration") {
          profile = await createAdmin.mutateAsync({ firstName, lastName, email, phone, adminRole, agenceId: selectedAgenceId === "none" ? null : selectedAgenceId });
        } else {
          profile = await createTechnician.mutateAsync({ firstName, lastName, email, phone, hasDrivingLicense, agenceId: selectedAgenceId === "none" ? null : selectedAgenceId });
        }
        setCreatedProfileId(profile.id);
        toast({ title: "Collaborateur créé", description: `${firstName} ${lastName}` });
        setActiveTab("documents");
      }
    } catch (e: any) {
      if (e.status === 409 && e.body?.code === "PROFILE_EXISTS") {
        setConflictProfile({ profileId: e.body.profileId, isActive: e.body.isActive });
        return;
      }
      toast({ variant: "destructive", title: "Erreur", description: e.message });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!createdProfileId || !e.target.files) return;
    for (const file of Array.from(e.target.files)) {
      try {
        await uploadDocument.mutateAsync({ profileId: createdProfileId, file });
      } catch {
        toast({ variant: "destructive", title: "Erreur", description: `Échec upload ${file.name}` });
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteDoc = async (filename: string) => {
    if (!createdProfileId) return;
    try {
      await deleteDocument.mutateAsync({ profileId: createdProfileId, filename });
    } catch {
      toast({ variant: "destructive", title: "Erreur suppression" });
    }
  };

  const handleSaveWeekSchedule = async () => {
    if (!createdProfileId) return;
    const ws = toDateString(scheduleMonday);
    const existing = scheduleMap.get(`${createdProfileId}_${ws}`);
    try {
      if (existing) {
        await updateSchedule.mutateAsync({ id: existing.id, ...weekSchedule } as any);
      } else {
        await createSchedule.mutateAsync({ technicianId: createdProfileId, weekStart: ws, ...weekSchedule } as any);
      }
      toast({ title: "Semaine enregistrée" });
    } catch {
      toast({ variant: "destructive", title: "Erreur enregistrement" });
    }
  };

  const handleApplyRange = async () => {
    if (!createdProfileId || !rangeStart || !rangeEnd) {
      toast({ variant: "destructive", title: "Erreur", description: "Sélectionnez les dates de début et fin" });
      return;
    }
    const mondays = getMondaysInRange(new Date(rangeStart), new Date(rangeEnd));
    const startD = new Date(rangeStart);
    const endD = new Date(rangeEnd);

    const weeks = mondays.map(monday => {
      const week: Record<string, string> = { weekStart: toDateString(monday) };
      DAYS.forEach((day, i) => {
        const dayDate = new Date(monday);
        dayDate.setDate(dayDate.getDate() + i);
        if (dayDate >= startD && dayDate <= endD) {
          week[day.key] = rangeStatus;
        } else {
          const existingSched = scheduleMap.get(`${createdProfileId}_${toDateString(monday)}`);
          week[day.key] = existingSched ? (existingSched[day.key as keyof TechnicianSchedule] as string) : "bureau";
        }
      });
      return week;
    });

    try {
      await batchSchedules.mutateAsync({ technicianId: createdProfileId, weeks: weeks as any });
      toast({ title: "Plage appliquée", description: `${mondays.length} semaine(s) mises à jour` });
    } catch {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleApplyTemplate = async () => {
    if (!createdProfileId || !templateStart || !templateEnd) {
      toast({ variant: "destructive", title: "Erreur", description: "Sélectionnez les dates" });
      return;
    }
    const mondays = getMondaysInRange(new Date(templateStart), new Date(templateEnd));
    const weeks = mondays.map(monday => ({
      weekStart: toDateString(monday),
      ...templateWeek,
    }));

    try {
      await batchSchedules.mutateAsync({ technicianId: createdProfileId, weeks });
      toast({ title: "Semaine type appliquée", description: `${mondays.length} semaine(s)` });
    } catch {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const loadWeekFromSchedule = useCallback((monday: Date) => {
    if (!createdProfileId) return;
    const ws = toDateString(monday);
    const existing = scheduleMap.get(`${createdProfileId}_${ws}`);
    if (existing) {
      setWeekSchedule({
        mondayStatus: existing.mondayStatus,
        tuesdayStatus: existing.tuesdayStatus,
        wednesdayStatus: existing.wednesdayStatus,
        thursdayStatus: existing.thursdayStatus,
        fridayStatus: existing.fridayStatus,
      });
    } else {
      setWeekSchedule({
        mondayStatus: "bureau", tuesdayStatus: "bureau", wednesdayStatus: "bureau",
        thursdayStatus: "bureau", fridayStatus: "bureau",
      });
    }
    setScheduleMonday(monday);
  }, [createdProfileId, scheduleMap]);

  const annualWeeks = useMemo(() => getWeeksOfYear(annualYear), [annualYear]);
  const annualByMonth = useMemo(() => {
    const months: { month: number; weeks: Date[] }[] = [];
    let currentMonth = -1;
    annualWeeks.forEach(w => {
      const m = w.getMonth();
      if (m !== currentMonth) {
        months.push({ month: m, weeks: [] });
        currentMonth = m;
      }
      months[months.length - 1].weeks.push(w);
    });
    return months;
  }, [annualWeeks]);

  const dialogTitle = isEdit
    ? `Modifier — ${editingMembre?.firstName} ${editingMembre?.lastName}`
    : "Ajouter un collaborateur";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="text-dialog-title">{dialogTitle}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Modifiez les informations, documents et planning." : "Créez un nouveau membre de l'équipe."}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="info" data-testid="tab-info">Informations</TabsTrigger>
            <TabsTrigger value="documents" disabled={!isEdit && !createdProfileId} data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="planning" disabled={!isEdit && !createdProfileId} data-testid="tab-planning">Planning</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-5 mt-4">
            <div className="flex items-center gap-4">
              <div className="relative group">
                <Avatar className="h-20 w-20 border-2 shadow-md">
                  <AvatarImage src={avatarPreview || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xl font-bold">
                    {firstName?.[0] || "?"}{lastName?.[0] || ""}
                  </AvatarFallback>
                </Avatar>
                <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <Camera className="w-5 h-5 text-white" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} data-testid="input-avatar" />
                </label>
              </div>
              <div className="text-sm text-muted-foreground">Cliquez pour {isEdit ? "modifier" : "ajouter"} la photo</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Jean" data-testid="input-firstname" />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Dupont" data-testid="input-lastname" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email {isEdit ? "" : "*"}</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jean@jobotech.fr" data-testid="input-email" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="06 12 34 56 78" data-testid="input-phone" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Service *</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger data-testid="select-role">
                  <SelectValue placeholder="Choisir le service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technicien">
                    <div className="flex items-center gap-2"><HardHat className="w-4 h-4" />Technicien</div>
                  </SelectItem>
                  <SelectItem value="commercial">
                    <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4" />Commercial</div>
                  </SelectItem>
                  <SelectItem value="administration">
                    <div className="flex items-center gap-2"><Briefcase className="w-4 h-4" />Administratif</div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2"><Crown className="w-4 h-4" />Dirigeant</div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Agence</Label>
              <Select value={selectedAgenceId} onValueChange={setSelectedAgenceId}>
                <SelectTrigger data-testid="select-agence"><SelectValue placeholder="Non rattaché" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non rattaché</SelectItem>
                  {(agences || []).filter(a => a.actif || a.id === selectedAgenceId).map(agence => (
                    <SelectItem key={agence.id} value={agence.id}>{agence.libelle} — {agence.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Rôles secondaires</Label>
              <p className="text-xs text-muted-foreground">Fonctions supplémentaires exercées en parallèle</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {([
                  { key: "technicien", label: "Technicien", icon: HardHat },
                  { key: "commercial", label: "Commercial", icon: TrendingUp },
                  { key: "administration", label: "Administratif", icon: Briefcase },
                  { key: "gerant", label: "Gérant", icon: Crown },
                ] as const).filter(r => r.key !== selectedRole).map(r => {
                  const isSelected = rolesSecondaires.includes(r.key);
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setRolesSecondaires(prev =>
                        isSelected ? prev.filter(x => x !== r.key) : [...prev, r.key]
                      )}
                      data-testid={`role-sec-${r.key}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-all ${
                        isSelected
                          ? "bg-primary/10 border-primary text-primary"
                          : "bg-secondary/20 border-transparent text-muted-foreground hover:bg-secondary/40"
                      }`}
                    >
                      <r.icon className="w-3.5 h-3.5" />{r.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedRole === "commercial" && (
              <div className="space-y-2">
                <Label>Statut commercial</Label>
                <Select value={commercialStatus} onValueChange={setCommercialStatus}>
                  <SelectTrigger data-testid="select-commercial-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(COMMERCIAL_STATUS_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedRole === "administration" && (
              <div className="space-y-2">
                <Label>Poste / Rôle</Label>
                <Select value={adminRole} onValueChange={setAdminRole}>
                  <SelectTrigger data-testid="select-admin-role">
                    <SelectValue placeholder="Sélectionner un poste..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ADMIN_ROLE_PRESETS.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2 rounded-lg border p-4 bg-muted/30">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Niveau d'accès plateforme</Label>
              </div>
              {isSuperAdminUser ? (
                <>
                  <Select value={selectedAccessRole} onValueChange={setSelectedAccessRole}>
                    <SelectTrigger data-testid="select-access-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACCESS_ROLE_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Détermine les droits d'accès sur la plateforme interne.</p>
                </>
              ) : (
                <div className="flex items-center gap-2" data-testid="display-access-role">
                  <Badge
                    variant="outline"
                    className={`text-xs px-2 py-0.5 border ${accessRoleBadgeColor((editingMembre as any)?.accessRole)}`}
                  >
                    <Shield className="w-3 h-3 mr-1" />
                    {ACCESS_ROLE_LABELS[((editingMembre as any)?.accessRole || "technicien") as keyof typeof ACCESS_ROLE_LABELS] || "Technicien"}
                  </Badge>
                  <span className="text-xs text-muted-foreground">Niveau d'accès actuel (lecture seule)</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label>Permis de conduire</Label>
                <p className="text-xs text-muted-foreground">Permis B</p>
              </div>
              <Switch checked={hasDrivingLicense} onCheckedChange={setHasDrivingLicense} data-testid="switch-license" />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label>Intervenant terrain</Label>
                <p className="text-xs text-muted-foreground">Assignable aux tickets SAV (indépendant du rôle d'accès)</p>
              </div>
              <Switch
                checked={intervenantTerrain}
                onCheckedChange={setIntervenantTerrain}
                data-testid="switch-intervenant-terrain"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label>Gestion du planning</Label>
                <p className="text-xs text-muted-foreground">Peut modifier et supprimer toutes les missions (indépendant du rôle d'accès)</p>
              </div>
              <Switch
                checked={gestionPlanning}
                onCheckedChange={setGestionPlanning}
                data-testid="switch-gestion-planning"
              />
            </div>

            {conflictProfile && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-300">
                    <span className="font-semibold">Email déjà utilisé</span>
                    {" — "}
                    {conflictProfile.isActive
                      ? "Un membre actif avec cet email existe déjà dans l'équipe."
                      : "Un membre inactif avec cet email existe déjà."}
                  </div>
                </div>
                <div className="flex gap-2">
                  {conflictProfile.isActive ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/40"
                        onClick={() => { onOpenChange(false); window.location.href = `/equipe?highlight=${conflictProfile.profileId}`; }}
                        data-testid="button-conflict-voir"
                      >
                        Voir le profil
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/40"
                        onClick={async () => {
                          try {
                            const res = await authFetch(`/api/profiles/${conflictProfile.profileId}/invite`, { method: "POST" });
                            if (res.ok) {
                              toast({ title: "Invitation envoyée", description: "L'email d'invitation a été renvoyé." });
                              onOpenChange(false);
                            } else {
                              const err = await res.json();
                              toast({ variant: "destructive", title: "Erreur invitation", description: err.message });
                            }
                          } catch {
                            toast({ variant: "destructive", title: "Erreur invitation" });
                          }
                        }}
                        data-testid="button-conflict-inviter"
                      >
                        <Mail className="w-3.5 h-3.5 mr-1.5" />Inviter
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/40"
                      onClick={() => { onOpenChange(false); window.location.href = `/equipe?highlight=${conflictProfile.profileId}&tab=inactifs`; }}
                      data-testid="button-conflict-reactiver"
                    >
                      <UserCheck className="w-3.5 h-3.5 mr-1.5" />Réactiver ce membre
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground"
                    onClick={() => setConflictProfile(null)}
                    data-testid="button-conflict-dismiss"
                  >
                    Ignorer
                  </Button>
                </div>
              </div>
            )}

            <Button
              className="w-full"
              onClick={() => { setConflictProfile(null); handleSaveProfile(); }}
              disabled={isPending}
              data-testid="button-save-profile"
            >
              {isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</>
              ) : isEdit ? (
                <><Save className="w-4 h-4 mr-2" />Enregistrer les modifications</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" />Créer le collaborateur</>
              )}
            </Button>
          </TabsContent>

          <TabsContent value="documents" className="space-y-5 mt-4">
            <div className="rounded-lg border-2 border-dashed p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-3">CNI recto/verso, permis, contrat, diplômes...</p>
              <label>
                <Button variant="outline" asChild className="cursor-pointer">
                  <span><Upload className="w-4 h-4 mr-2" />Choisir des fichiers</span>
                </Button>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileUpload} data-testid="input-documents" />
              </label>
              {uploadDocument.isPending && (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />Upload en cours...
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Documents uploadés</h3>
              {(!documents || documents.length === 0) ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Aucun document</p>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc: DocumentInfo) => (
                    <div key={doc.name} className="flex items-center justify-between rounded-lg border p-3" data-testid={`doc-${doc.name}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{doc.name}</p>
                          <p className="text-xs text-muted-foreground">{doc.size ? `${(doc.size / 1024).toFixed(1)} Ko` : ""}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteDoc(doc.name)} disabled={deleteDocument.isPending}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="planning" className="space-y-4 mt-4">
            <div className="flex gap-1 bg-secondary/30 rounded-lg p-1">
              {([
                { key: "week", icon: Calendar, label: "Semaine" },
                { key: "annual", icon: CalendarRange, label: "Vue annuelle" },
                { key: "range", icon: CalendarRange, label: "Plage de dates" },
                { key: "template", icon: LayoutTemplate, label: "Semaine type" },
              ] as const).map(m => (
                <button
                  key={m.key}
                  onClick={() => setPlanningMode(m.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                    planningMode === m.key ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`planning-mode-${m.key}`}
                >
                  <m.icon className="w-3.5 h-3.5" />
                  {m.label}
                </button>
              ))}
            </div>

            {planningMode === "week" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => loadWeekFromSchedule((() => { const d = new Date(scheduleMonday); d.setDate(d.getDate() - 7); return d; })())}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-semibold">{formatWeekRange(scheduleMonday)}</span>
                  <Button variant="ghost" size="sm" onClick={() => loadWeekFromSchedule((() => { const d = new Date(scheduleMonday); d.setDate(d.getDate() + 7); return d; })())}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  {DAYS.map(day => (
                    <div key={day.key} className="flex items-center justify-between rounded-lg border p-3">
                      <span className="text-sm font-medium w-20">{day.full}</span>
                      <div className="flex gap-2 flex-wrap">
                        {Object.entries(STATUS_CONFIG).map(([key, scfg]) => {
                          const Icon = scfg.icon;
                          const isActive = weekSchedule[day.key] === key;
                          return (
                            <button key={key}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-all ${
                                isActive ? `${scfg.bg} ${scfg.text} ${scfg.border} ring-2 ring-offset-1 ring-current` : "bg-secondary/20 text-muted-foreground border-transparent hover:bg-secondary/40"
                              }`}
                              onClick={() => setWeekSchedule(prev => ({ ...prev, [day.key]: key }))}
                              data-testid={`sched-${day.key}-${key}`}
                            >
                              <Icon className="w-3.5 h-3.5" />{scfg.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button className="flex-1" onClick={handleSaveWeekSchedule}
                    disabled={createSchedule.isPending || updateSchedule.isPending}>
                    {(createSchedule.isPending || updateSchedule.isPending) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer
                  </Button>
                  <Button variant="outline" onClick={() => {
                    const next = new Date(scheduleMonday);
                    next.setDate(next.getDate() + 7);
                    setScheduleMonday(next);
                    toast({ title: "Planning copié vers semaine suivante" });
                  }}>
                    <Copy className="w-4 h-4 mr-2" />Copier
                  </Button>
                </div>
              </div>
            )}

            {planningMode === "annual" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => setAnnualYear(y => y - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-bold">{annualYear}</span>
                  <Button variant="ghost" size="sm" onClick={() => setAnnualYear(y => y + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2 flex-wrap">
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <div key={key} className="flex items-center gap-1.5">
                      <div className={`w-3 h-3 rounded-sm ${cfg.color}`} />
                      <span>{cfg.label}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-gray-200 dark:bg-gray-700" />
                    <span>Non planifié</span>
                  </div>
                </div>

                <TooltipProvider delayDuration={100}>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                    {annualByMonth.map(({ month, weeks }) => (
                      <div key={month}>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">{MONTHS_FR[month]}</p>
                        <div className="flex flex-wrap gap-1">
                          {weeks.map(monday => {
                            const ws = toDateString(monday);
                            const sched = createdProfileId ? scheduleMap.get(`${createdProfileId}_${ws}`) : undefined;
                            const isSelected = selectedAnnualWeek && toDateString(selectedAnnualWeek) === ws;

                            return (
                              <Tooltip key={ws}>
                                <TooltipTrigger asChild>
                                  <button
                                    className={`flex gap-px rounded overflow-hidden border transition-all ${isSelected ? "ring-2 ring-primary ring-offset-1" : "hover:ring-1 hover:ring-primary/50"}`}
                                    onClick={() => {
                                      setSelectedAnnualWeek(monday);
                                      loadWeekFromSchedule(monday);
                                      setPlanningMode("week");
                                    }}
                                    data-testid={`annual-week-${ws}`}
                                  >
                                    {DAYS.map((day, i) => {
                                      const dayDate = new Date(monday);
                                      dayDate.setDate(dayDate.getDate() + i);
                                      const dateISO = toDateString(dayDate);
                                      const altStatut = createdProfileId ? alternanceMap.get(`${createdProfileId}_${dateISO}`) : undefined;
                                      const resolved = resolveDayStatus(sched || null, day.key, altStatut);
                                      const color = STATUS_CONFIG[resolved as keyof typeof STATUS_CONFIG]
                                        ? STATUS_CONFIG[resolved as keyof typeof STATUS_CONFIG].color
                                        : "bg-gray-200 dark:bg-gray-700";
                                      return <div key={i} className={`w-2.5 h-4 ${color}`} />;
                                    })}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  <p className="font-semibold">S{getWeekNumber(monday)}</p>
                                  <p>{formatWeekRange(monday)}</p>
                                  {sched && DAYS.map((day, i) => {
                                    const dayDate = new Date(monday);
                                    dayDate.setDate(dayDate.getDate() + i);
                                    const dateISO = toDateString(dayDate);
                                    const altStatut = createdProfileId ? alternanceMap.get(`${createdProfileId}_${dateISO}`) : undefined;
                                    const s = resolveDayStatus(sched, day.key, altStatut);
                                    return <p key={day.key}>{day.label}: {STATUS_CONFIG[s as keyof typeof STATUS_CONFIG]?.label || s}</p>;
                                  })}
                                  {!sched && <p className="text-muted-foreground">Non planifié</p>}
                                </TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </TooltipProvider>
              </div>
            )}

            {planningMode === "range" && (
              <div className="space-y-4">
                <div className="rounded-lg border p-4 space-y-4">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <CalendarRange className="w-4 h-4" />
                    Appliquer un statut sur une plage de dates
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Idéal pour saisir les congés, semaines école ou absences longue durée.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Date début</Label>
                      <Input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} data-testid="input-range-start" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Date fin</Label>
                      <Input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} data-testid="input-range-end" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Statut à appliquer</Label>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                        const Icon = cfg.icon;
                        return (
                          <button key={key}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-xs font-medium transition-all ${
                              rangeStatus === key ? `${cfg.bg} ${cfg.text} ${cfg.border} ring-2 ring-offset-1 ring-current` : "bg-secondary/20 text-muted-foreground border-transparent hover:bg-secondary/40"
                            }`}
                            onClick={() => setRangeStatus(key)}
                            data-testid={`range-status-${key}`}
                          >
                            <Icon className="w-3.5 h-3.5" />{cfg.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {rangeStart && rangeEnd && (
                    <p className="text-xs text-muted-foreground">
                      {getMondaysInRange(new Date(rangeStart), new Date(rangeEnd)).length} semaine(s) concernée(s)
                    </p>
                  )}
                  <Button className="w-full" onClick={handleApplyRange} disabled={batchSchedules.isPending} data-testid="button-apply-range">
                    {batchSchedules.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Appliquer la plage
                  </Button>
                </div>
              </div>
            )}

            {planningMode === "template" && (
              <div className="space-y-4">
                <div className="rounded-lg border p-4 space-y-4">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <LayoutTemplate className="w-4 h-4" />
                    Semaine type récurrente
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Définissez une semaine type et appliquez-la sur une plage de dates.
                  </p>

                  <div className="space-y-2">
                    {DAYS.map(day => (
                      <div key={day.key} className="flex items-center justify-between rounded-lg border p-3">
                        <span className="text-sm font-medium w-20">{day.full}</span>
                        <div className="flex gap-2 flex-wrap">
                          {Object.entries(STATUS_CONFIG).map(([key, scfg]) => {
                            const Icon = scfg.icon;
                            const isActive = templateWeek[day.key] === key;
                            return (
                              <button key={key}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-all ${
                                  isActive ? `${scfg.bg} ${scfg.text} ${scfg.border} ring-2 ring-offset-1 ring-current` : "bg-secondary/20 text-muted-foreground border-transparent hover:bg-secondary/40"
                                }`}
                                onClick={() => setTemplateWeek(prev => ({ ...prev, [day.key]: key }))}
                                data-testid={`template-${day.key}-${key}`}
                              >
                                <Icon className="w-3.5 h-3.5" />{scfg.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Appliquer du</Label>
                      <Input type="date" value={templateStart} onChange={e => setTemplateStart(e.target.value)} data-testid="input-template-start" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Au</Label>
                      <Input type="date" value={templateEnd} onChange={e => setTemplateEnd(e.target.value)} data-testid="input-template-end" />
                    </div>
                  </div>

                  {templateStart && templateEnd && (
                    <p className="text-xs text-muted-foreground">
                      {getMondaysInRange(new Date(templateStart), new Date(templateEnd)).length} semaine(s) concernée(s)
                    </p>
                  )}

                  <Button className="w-full" onClick={handleApplyTemplate} disabled={batchSchedules.isPending} data-testid="button-apply-template">
                    {batchSchedules.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Appliquer la semaine type
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default function EquipeList() {
  const { profile: currentUser } = useAuth();
  const viewerLevel = getRoleLevel((currentUser as any)?.accessRole);
  const canEditPresence = viewerLevel >= 2;
  const canEditProfile = viewerLevel >= 3;
  const isAdminPlus = viewerLevel >= 3;
  const isSuperAdminUser = isSuperAdmin((currentUser as any)?.accessRole);

  const { data: allProfiles, isLoading } = useProfiles();
  const { data: agences } = useAgences();
  const { data: schedules } = useTechnicianSchedules();
  const alternanceMapGrille = useAlternanceJours();
  const deleteProfile = useDeleteProfile();
  const { toast } = useToast();
  const queryClientMain = useQueryClient();

  const inviteProfile = useMutation({
    mutationFn: async (profileId: string) => {
      const res = await authFetch(`/api/profiles/${profileId}/invite`, { method: 'POST' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message); }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Invitation envoyée", description: "Un email d'invitation a été envoyé au collaborateur." });
      queryClientMain.invalidateQueries({ queryKey: ['/api/profiles'] });
    },
    onError: (e: Error) => {
      toast({ variant: "destructive", title: "Erreur invitation", description: e.message });
    },
  });

  const [serviceFilter, setServiceFilter] = useState<ServiceFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("actifs");
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingMembre, setEditingMembre] = useState<Profile | null>(null);
  const [currentMonday, setCurrentMonday] = useState(() => getMonday(new Date()));
  const [deletingMembre, setDeletingMembre] = useState<Profile | null>(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState<"check" | "confirm">("check");
  const [deleteLinkedItems, setDeleteLinkedItems] = useState<string[]>([]);
  const [deleteSecondConfirm, setDeleteSecondConfirm] = useState(false);

  const allActiveCount = useMemo(() => (allProfiles ?? []).filter(p => p.isActive !== false).length, [allProfiles]);
  const allInactiveCount = useMemo(() => (allProfiles ?? []).filter(p => p.isActive === false).length, [allProfiles]);

  const membres = useMemo(() => {
    if (!allProfiles) return [];
    if (statusFilter === "actifs") return allProfiles.filter(p => p.isActive !== false);
    if (statusFilter === "inactifs") return allProfiles.filter(p => p.isActive === false);
    return allProfiles;
  }, [allProfiles, statusFilter]);

  const filteredMembres = useMemo(() => {
    return membres.filter(p => {
      if (serviceFilter === "all") return true;
      if (serviceFilter === "dirigeant") return p.role === "admin" || p.role === "planification";
      return p.role === serviceFilter;
    });
  }, [membres, serviceFilter]);

  const normalize = (s: string) =>
    (s ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const searchedMembres = useMemo(() => {
    const tokens = normalize(search).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return filteredMembres;
    return filteredMembres.filter(c => {
      const haystack = normalize(`${c.firstName ?? ""} ${c.lastName ?? ""} ${(c as any).email ?? ""} ${c.role ?? ""}`);
      return tokens.every(t => haystack.includes(t));
    });
  }, [filteredMembres, search]);

  const todayKey = useMemo((): DayKey => {
    const day = new Date().getDay();
    const map: Record<number, DayKey> = {
      1: "mondayStatus", 2: "tuesdayStatus", 3: "wednesdayStatus",
      4: "thursdayStatus", 5: "fridayStatus",
    };
    return map[day] || "mondayStatus";
  }, []);

  const scheduleMap = useMemo(() => {
    const map = new Map<string, TechnicianSchedule>();
    schedules?.forEach(s => {
      map.set(`${s.technicianId}_${s.weekStart}`, s);
    });
    return map;
  }, [schedules]);

  const getTodayStatus = (profileId: string): string => {
    const ws = toDateString(getMonday(new Date()));
    const sched = scheduleMap.get(`${profileId}_${ws}`);
    const todayISO = toDateString(new Date());
    const altStatut = alternanceMapGrille.get(`${profileId}_${todayISO}`);
    return resolveDayStatus(sched, todayKey, altStatut);
  };

  const getScheduleForMembre = (profileId: string): TechnicianSchedule | undefined => {
    const ws = toDateString(currentMonday);
    return scheduleMap.get(`${profileId}_${ws}`);
  };

  const upsertScheduleDay = useUpsertScheduleDay();
  const updateScheduleMutation = useUpdateSchedule();

  const handleStatusChange = async (profileId: string, dayKey: string, newStatus: string) => {
    const ws = toDateString(currentMonday);
    const dayName = dayKey.replace("Status", "") as "monday" | "tuesday" | "wednesday" | "thursday" | "friday";

    // Optimistic update
    const SCHED_KEY = "/api/schedules";
    const previous = queryClientMain.getQueryData<any[]>([SCHED_KEY]);
    queryClientMain.setQueryData<any[]>([SCHED_KEY], (old = []) => {
      const existing = old.find(s => s.technicianId === profileId && s.weekStart === ws);
      if (existing) {
        return old.map(s => s.id === existing.id ? { ...s, [dayKey]: newStatus } : s);
      }
      return [...old, {
        id: `optimistic-${profileId}-${ws}`,
        technicianId: profileId,
        weekStart: ws,
        mondayStatus: "bureau", tuesdayStatus: "bureau", wednesdayStatus: "bureau",
        thursdayStatus: "bureau", fridayStatus: "bureau",
        [dayKey]: newStatus,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }];
    });

    try {
      await upsertScheduleDay.mutateAsync({ technician_id: profileId, week_start: ws, day: dayName, status: newStatus });
    } catch (err) {
      queryClientMain.setQueryData([SCHED_KEY], previous);
      toast({
        variant: "destructive",
        title: "Erreur de mise à jour",
        description: err instanceof Error ? err.message : "Impossible de mettre à jour le statut de présence",
      });
      console.error("[handleStatusChange]", err);
    }
  };

  const prevWeek = () => {
    const d = new Date(currentMonday);
    d.setDate(d.getDate() - 7);
    setCurrentMonday(d);
  };

  const nextWeek = () => {
    const d = new Date(currentMonday);
    d.setDate(d.getDate() + 7);
    setCurrentMonday(d);
  };

  const goToday = () => setCurrentMonday(getMonday(new Date()));

  const openAdd = () => {
    setEditingMembre(null);
    setShowDialog(true);
  };

  const openEdit = (membre: Profile) => {
    setEditingMembre(membre);
    setShowDialog(true);
  };

  const reactivateProfile = useUpdateProfile();

  const openDelete = async (membre: Profile) => {
    setDeletingMembre(membre);
    setDeleteConfirmStep("check");
    setDeleteLinkedItems([]);
    setDeleteSecondConfirm(false);
    try {
      const res = await authFetch(`/api/profiles/${membre.id}/check-links`);
      const data = await res.json();
      setDeleteLinkedItems(data.linkedItems || []);
    } catch {
      setDeleteLinkedItems([]);
    }
    setDeleteConfirmStep("confirm");
  };

  const confirmDelete = async () => {
    if (!deletingMembre) return;
    try {
      await deleteProfile.mutateAsync(deletingMembre.id);
      toast({ title: "Membre désactivé", description: `${deletingMembre.firstName} ${deletingMembre.lastName} n'a plus accès à la plateforme.` });
      setDeletingMembre(null);
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur", description: e.message });
    }
  };

  const countByFilter = (filter: ServiceFilter): number => {
    return membres.filter(p => {
      if (filter === "all") return true;
      if (filter === "dirigeant") return p.role === "admin" || p.role === "planification";
      return p.role === filter;
    }).length;
  };

  const isAdmin = canDo((currentUser as any)?.accessRole);
  const [pageTab, setPageTab] = useState<"planning" | "pudu">("planning");

  // ── Pudu Cloud sync (async + polling) ────────────────────────────────────────
  const [puduLaunching, setPuduLaunching] = useState(false);

  const { data: puduSyncStatus, refetch: refetchPuduStatus } = useQuery<any>({
    queryKey: ["/api/pudu/sync-status"],
    queryFn: () => authFetch("/api/pudu/sync-status").then(r => r.json()),
    refetchInterval: (query) => query.state.data?.status === "running" ? 3000 : false,
  });

  const puduIsRunning = puduSyncStatus?.status === "running" || puduLaunching;

  const handlePuduSync = async () => {
    if (puduIsRunning) return;
    setPuduLaunching(true);
    try {
      const res = await authFetch("/api/pudu/sync-robots", { method: "POST" });
      const data = await res.json();
      if (res.status === 409) {
        toast({ title: "Synchro déjà en cours", description: data.message, variant: "destructive" });
      } else if (!res.ok) {
        throw new Error(data.message || `Erreur ${res.status}`);
      } else {
        toast({ title: "Synchronisation lancée", description: "Traitement en arrière-plan…" });
      }
      await refetchPuduStatus();
    } catch (e: any) {
      toast({ title: "Erreur Pudu", description: e.message, variant: "destructive" });
    } finally {
      setPuduLaunching(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Équipe</h1>
          {pageTab === "planning" && <p className="text-muted-foreground mt-1">Planning de présence hebdomadaire</p>}
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Tabs value={pageTab} onValueChange={(v) => setPageTab(v as "planning" | "pudu")}>
              <TabsList>
                <TabsTrigger value="planning" data-testid="tab-planning-equipe">Planning équipe</TabsTrigger>
                <TabsTrigger value="pudu" data-testid="tab-pudu-cloud">Pudu Cloud</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          {pageTab === "planning" && (
            <>
              <Badge variant="outline" className="gap-1">
                <Users className="w-3 h-3" />
                {filteredMembres.length} collaborateur{filteredMembres.length > 1 ? "s" : ""}
              </Badge>
              {canEditProfile && (
                <Button onClick={openAdd} data-testid="button-add-membre">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un collaborateur
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {isAdmin && pageTab === "pudu" && (
        <div className="max-w-2xl space-y-6">
          <div className="bg-card border rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Bot className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-lg">Synchronisation Pudu Cloud</h2>
                <p className="text-sm text-muted-foreground">Importe les robots depuis les magasins Pudu Cloud et les associe aux clients</p>
              </div>
            </div>

            <Button
              onClick={handlePuduSync}
              disabled={puduIsRunning}
              data-testid="button-pudu-sync"
              className="gap-2"
            >
              {puduIsRunning ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Synchronisation en cours…</>
              ) : (
                <><RefreshCw className="w-4 h-4" />Synchroniser les robots Pudu</>
              )}
            </Button>
          </div>

          {puduSyncStatus && puduSyncStatus.status !== "never" && (
            <div className={`rounded-xl border p-5 space-y-3 ${
              puduSyncStatus.status === "running"
                ? "bg-blue-500/5 border-blue-200 dark:border-blue-800/50"
                : puduSyncStatus.status === "completed"
                ? "bg-green-500/5 border-green-200 dark:border-green-800/50"
                : "bg-destructive/5 border-destructive/20"
            }`}>
              <div className="flex items-center gap-2">
                {puduSyncStatus.status === "running" && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                {puduSyncStatus.status === "completed" && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                {puduSyncStatus.status === "error" && <AlertTriangle className="w-4 h-4 text-destructive" />}
                <span className="font-medium text-sm">
                  {puduSyncStatus.status === "running" && "Synchronisation en cours…"}
                  {puduSyncStatus.status === "completed" && "Synchronisation terminée"}
                  {puduSyncStatus.status === "error" && "Erreur de synchronisation"}
                </span>
                {puduSyncStatus.started_at && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {new Date(puduSyncStatus.started_at).toLocaleTimeString("fr-FR")}
                  </span>
                )}
              </div>
              {(puduSyncStatus.status === "running" || puduSyncStatus.status === "completed") && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-background/60 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold tabular-nums">{puduSyncStatus.shops_synced ?? 0}</p>
                    <p className="text-xs text-muted-foreground">Magasins</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold tabular-nums text-blue-600">{puduSyncStatus.robots_synced ?? 0}</p>
                    <p className="text-xs text-muted-foreground">Robots</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold tabular-nums text-purple-600">{puduSyncStatus.resellers_matched ?? 0}</p>
                    <p className="text-xs text-muted-foreground">Revendeurs</p>
                  </div>
                </div>
              )}
              {puduSyncStatus.status === "running" && (
                <div className="w-full h-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: "60%" }} />
                </div>
              )}
              {puduSyncStatus.status === "error" && puduSyncStatus.error_message && (
                <p className="text-xs text-destructive">{puduSyncStatus.error_message}</p>
              )}
            </div>
          )}
        </div>
      )}

      {pageTab === "planning" && (<>

      {/* Filtre tri-état Actifs / Inactifs / Tous + recherche */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-card border rounded-xl p-1.5 shadow-sm">
          {([
            { key: "actifs" as StatusFilter, label: "Actifs", icon: UserCheck, count: allActiveCount },
            { key: "inactifs" as StatusFilter, label: "Inactifs", icon: CircleSlash, count: allInactiveCount },
            { key: "tous" as StatusFilter, label: "Tous", icon: Users, count: allActiveCount + allInactiveCount },
          ]).map(f => {
            const active = statusFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  active ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                }`}
                data-testid={`status-filter-${f.key}`}
              >
                <f.icon className="w-3.5 h-3.5" />
                {f.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                  {f.count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-xs ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un collaborateur…"
            className="w-full pl-9 pr-8 py-2 text-sm rounded-xl border bg-card shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
            data-testid="input-search-membre"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="button-clear-search"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 bg-card border rounded-xl p-1.5 shadow-sm flex-wrap">
        {SERVICE_FILTERS.map(f => {
          const count = countByFilter(f.key);
          const isActive = serviceFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setServiceFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
              }`}
              data-testid={`filter-${f.key}`}
            >
              <f.icon className="w-3.5 h-3.5" />
              {f.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between bg-card rounded-xl border p-3 shadow-sm">
        <Button variant="ghost" size="sm" onClick={prevWeek} data-testid="button-prev-week">
          <ChevronLeft className="w-4 h-4 mr-1" />Semaine préc.
        </Button>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={goToday} data-testid="button-today">Aujourd'hui</Button>
          <span className="text-sm font-semibold" data-testid="text-week-range">{formatWeekRange(currentMonday)}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={nextWeek} data-testid="button-next-week">
          Semaine suiv.<ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${cfg.color}`} />
            <span>{cfg.label}</span>
          </div>
        ))}
      </div>

      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/40 border-b">
                <th className="text-left px-4 py-3 text-sm font-semibold w-72">Collaborateur</th>
                {DAYS.map(day => (
                  <th key={day.key} className="text-center px-2 py-3 text-sm font-semibold min-w-[120px]">{day.label}</th>
                ))}
                {isAdminPlus && <th className="text-center px-3 py-3 text-sm font-semibold min-w-[110px]">Accès</th>}
                {canEditPresence && <th className="w-24" />}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6 + (isAdminPlus ? 1 : 0) + (canEditPresence ? 1 : 0)} className="text-center py-12 text-muted-foreground">Chargement...</td></tr>
              ) : searchedMembres.map((membre: Profile) => {
                const isInactive = membre.isActive === false;
                const schedule = getScheduleForMembre(membre.id);
                return (
                  <tr key={membre.id} className={`border-b last:border-b-0 transition-colors ${isInactive ? "opacity-55 bg-muted/20" : "hover:bg-secondary/10"}`} data-testid={`row-membre-${membre.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className={`h-9 w-9 border shadow-sm ${isInactive ? "grayscale" : ""}`}>
                          <AvatarImage src={membre.avatarUrl || undefined} />
                          <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">
                            {membre.firstName?.[0]}{membre.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{membre.firstName} {membre.lastName}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {isInactive && (
                              <Badge variant="outline" className="text-xs px-1.5 py-0 border bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-600">
                                <UserMinus className="w-2.5 h-2.5 mr-1" />Inactif
                              </Badge>
                            )}
                            <Badge variant="outline" className={`text-xs px-1.5 py-0 border ${serviceColor(membre.role)}`}>
                              {roleToService(membre.role)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              · {agences?.find(a => a.id === (membre as any).agenceId)?.libelle || "Non rattaché"}
                            </span>
                            {((membre as any).rolesSecondaires || []).map((rs: string) => (
                              <Badge key={rs} variant="outline" className={`text-xs px-1.5 py-0 border opacity-70 ${serviceColor(rs)}`}>
                                {roleToService(rs)}
                              </Badge>
                            ))}
                            {membre.hasDrivingLicense && (
                              <span className="text-xs text-muted-foreground">· Permis B</span>
                            )}
                            {(membre as any).intervenantTerrain && (
                              <Badge variant="outline" className="text-xs px-1.5 py-0 border bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-700">
                                Terrain
                              </Badge>
                            )}
                            {(membre as any).gestionPlanning && (
                              <Badge variant="outline" className="text-xs px-1.5 py-0 border bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700">
                                Planning
                              </Badge>
                            )}
                            {(membre as any).commercialStatus && membre.role === "commercial" && (
                              <span className="text-xs text-muted-foreground">
                                · {COMMERCIAL_STATUS_LABELS[(membre as any).commercialStatus] || (membre as any).commercialStatus}
                              </span>
                            )}
                            {(membre as any).adminRole && membre.role === "administration" && (
                              <span className="text-xs text-muted-foreground">· {(membre as any).adminRole}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    {DAYS.map((day, i) => {
                      const dayDate = new Date(currentMonday);
                      dayDate.setDate(dayDate.getDate() + i);
                      const dateISO = toDateString(dayDate);
                      const altStatut = alternanceMapGrille.get(`${membre.id}_${dateISO}`);
                      const status = resolveDayStatus(schedule, day.key, altStatut);
                      const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.bureau;
                      const Icon = cfg.icon;
                      return (
                        <td key={day.key} className="px-2 py-3 text-center">
                          {canEditPresence ? (
                            <Select value={status} onValueChange={(v) => handleStatusChange(membre.id, day.key, v)}>
                              <SelectTrigger className={`w-full border ${cfg.border} ${cfg.bg} ${cfg.text} text-xs font-medium h-9`} data-testid={`select-${membre.id}-${day.key}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                                  const Icon = cfg.icon;
                                  return (
                                    <SelectItem key={key} value={key}><div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5" />{cfg.label}</div></SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border ${cfg.border} ${cfg.bg} ${cfg.text} text-xs font-medium`}>
                              <Icon className="w-3.5 h-3.5" />{cfg.label}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    {isAdminPlus && (
                      <td className="px-3 py-3 text-center" data-testid={`cell-access-role-${membre.id}`}>
                        <Badge
                          variant="outline"
                          className={`text-xs px-2 py-0.5 border ${accessRoleBadgeColor((membre as any).accessRole)}`}
                        >
                          <Shield className="w-3 h-3 mr-1" />
                          {ACCESS_ROLE_LABELS[((membre as any).accessRole || "technicien") as keyof typeof ACCESS_ROLE_LABELS] || "Technicien"}
                        </Badge>
                      </td>
                    )}
                    {canEditPresence && (
                      <td className="px-2 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {isInactive ? (
                            /* Membre inactif : seul "Réactiver" disponible */
                            canEditProfile && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30"
                                    onClick={() => reactivateProfile.mutate({ id: membre.id, isActive: true })}
                                    disabled={reactivateProfile.isPending}
                                    data-testid={`button-reactivate-${membre.id}`}
                                  >
                                    <UserCheck className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">Réactiver ce membre</TooltipContent>
                              </Tooltip>
                            )
                          ) : (
                            /* Membre actif : actions normales */
                            <>
                              {canEditProfile && (
                                <Button variant="ghost" size="sm" onClick={() => openEdit(membre)} data-testid={`button-edit-${membre.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdminPlus && (membre as any).email && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                                      onClick={() => inviteProfile.mutate(membre.id)}
                                      disabled={inviteProfile.isPending}
                                      data-testid={`button-invite-${membre.id}`}
                                    >
                                      <Mail className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    {(membre as any).invitedAt
                                      ? `Réinviter (invité le ${new Date((membre as any).invitedAt).toLocaleDateString("fr-FR")})`
                                      : "Envoyer invitation"}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {canEditProfile && membre.id !== currentUser?.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => openDelete(membre)}
                                  data-testid={`button-delete-${membre.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {!isLoading && searchedMembres.length === 0 && (
                <tr><td colSpan={6 + (isAdminPlus ? 1 : 0) + (canEditPresence ? 1 : 0)} className="text-center py-12 text-muted-foreground">
                  {search.trim()
                    ? <span className="flex flex-col items-center gap-1"><Search className="w-5 h-5 mx-auto mb-1 opacity-40" />Aucun collaborateur trouvé pour « {search.trim()} »</span>
                    : "Aucun collaborateur dans ce service"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDialog && (
        <MembreFormDialog
          key={editingMembre?.id || "new"}
          open={showDialog}
          onOpenChange={setShowDialog}
          editingMembre={editingMembre}
          schedules={schedules || []}
        />
      )}

      <Dialog open={!!deletingMembre} onOpenChange={open => { if (!open) setDeletingMembre(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Supprimer {deletingMembre?.firstName} {deletingMembre?.lastName} ?
            </DialogTitle>
            <DialogDescription>
              Cette action désactivera le compte. L'accès à la plateforme sera révoqué et le membre n'apparaîtra plus dans l'équipe.
            </DialogDescription>
          </DialogHeader>

          {deleteConfirmStep === "check" && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Vérification des données liées...</span>
            </div>
          )}

          {deleteConfirmStep === "confirm" && (
            <div className="space-y-4">
              {deleteLinkedItems.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-medium text-sm">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Données actives liées à ce membre
                  </div>
                  <ul className="space-y-1 pl-6 list-disc">
                    {deleteLinkedItems.map((item, i) => (
                      <li key={i} className="text-sm text-amber-800 dark:text-amber-300">{item}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Ces données seront conservées pour l'historique mais ne seront plus assignées à ce membre.
                  </p>
                </div>
              )}

              {deleteLinkedItems.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Aucune donnée active liée à ce membre. La désactivation peut être effectuée sans risque.
                </p>
              )}

              {/* Double confirmation si liaisons actives */}
              {deleteLinkedItems.length > 0 && !deleteSecondConfirm && (
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setDeletingMembre(null)} data-testid="button-cancel-delete">
                    Annuler
                  </Button>
                  <Button
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/30"
                    onClick={() => setDeleteSecondConfirm(true)}
                    data-testid="button-confirm-delete-step1"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Désactiver malgré les liaisons →
                  </Button>
                </div>
              )}

              {deleteLinkedItems.length > 0 && deleteSecondConfirm && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive font-medium">
                    Confirmation finale requise — les données liées resteront dans l'historique.
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => { setDeletingMembre(null); setDeleteSecondConfirm(false); }} data-testid="button-cancel-delete">
                      Annuler
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={confirmDelete}
                      disabled={deleteProfile.isPending}
                      data-testid="button-confirm-delete"
                    >
                      {deleteProfile.isPending ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Désactivation...</>
                      ) : (
                        <><Trash2 className="w-4 h-4 mr-2" />Confirmer la désactivation</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {deleteLinkedItems.length === 0 && (
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setDeletingMembre(null)} data-testid="button-cancel-delete">
                    Annuler
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={confirmDelete}
                    disabled={deleteProfile.isPending}
                    data-testid="button-confirm-delete"
                  >
                    {deleteProfile.isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Désactivation...</>
                    ) : (
                      <><Trash2 className="w-4 h-4 mr-2" />Désactiver le membre</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
}
